import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  CSSProperties,
  ComponentProps,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  WheelEvent as ReactWheelEvent,
} from 'react';
import type { Limits, MediaRef, MessageDoc, UserDoc } from '../../../shared/types';
import {
  avatarOf,
  deletePost,
  editMessage,
  pinMessage,
  saveToPhone,
  sendLocation,
  sendText,
  setMessageReaction,
  unpinMessage,
  urlFor,
  type WithId,
} from '../data';
import {
  CHAT_NAME,
  MESSAGE_TEXT_MAX,
  dayKey,
  isQuietSender,
  shouldBeQuiet,
  shownMedia,
} from '../../../shared/constants';
import { summaryOf } from '../../../shared/summary';
import { QuoteThumb } from '../QuoteThumb';
import { EventTile, PinList, PinSheet } from '../sheets/PinSheets';
import { whenWords, type PinEvent } from '../../../shared/events';
import {
  clockTime,
  readChatSeenAt,
  timeMarkerFor,
  toDate,
  useThreadAround,
  useTypingSignal,
  useWhoIsTyping,
  type ChatState,
  type Pinned,
} from '../hooks';
import { typingLine } from '../typing';
import {
  blockSnaps,
  groupDailies,
  latestOf,
  messagesOf,
  threadSnapBacks,
  type Item,
} from '../chatGroups';
import { LinkCard } from '../LinkCard';
import { PollRow, PollSheet } from './Poll';
import { SnapChips, SnapConversation } from './Snap';
import { enqueueUpload } from '../uploadQueue';
import { renewMediaSession } from '../session';
import { OfflineNote } from '../OfflineNote';
import { takeShared } from '../shareTarget';
import { copyMessage } from '../media/clipboard';
import { checkVideo, inspectVideo } from '../media/video';
import { canRecord, startRecording, type Recorder } from '../media/audio';
import { go, swap, useBackCloses } from '../router';
import { EVERYONE, activeMention, mentionsMeSince, withLinks, withMentions } from '../mentions';
import {
  Avatar,
  IconCamera,
  IconVideo,
  IconClose,
  IconDown,
  IconImage,
  IconMic,
  IconSearch,
  IconForward,
  IconFile,
  IconPin,
  IconPlay,
  IconPlus,
  IconPoll,
  IconSend,
  IconDownload,
  MediaViewer,
  Sheet,
  ReactionBar,
  emojisOf,
  ReactedBy,
  Spinner,
  useHold,
} from '../ui';
import { Photo } from '../Photo';

/**
 * Whether Enter should make a newline rather than send.
 *
 * On a phone there is no shift to hold for the other one, and a composer that
 * grows to six lines is no use if the key that makes a second line sends
 * instead.
 */
const isTouch = () => navigator.maxTouchPoints > 0;

/** Drag past this and the message becomes the one you are replying to. */
const REPLY_AT = 56;
const DRAG_MAX = 84;
/** Movement below this is a tap, not a direction. */
const SLOP = 10;
const LONG_PRESS_MS = 420;

/** The unsent message, kept on this phone. */
const DRAFT_KEY = 'givisnap:chatDraft';

function readDraft(): string {
  try {
    return localStorage.getItem(DRAFT_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeDraft(text: string): void {
  try {
    if (text) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Blocked storage: the draft lasts as long as the screen does, as before.
  }
}

/*
 * How far the dock's shadow reaches above it (theme.css, .composer-dock::before)
 * and how much clear space the newest message keeps above that. The thread
 * used to leave 40px under a shadow reaching 76, so at the bottom the newest
 * message sat in the fade -- most plainly the one you had just sent.
 */
export const VEIL_REACH = 56;
/** A scroll this soon after a finger, wheel or key is one somebody made. */
const USER_SCROLL_MS = 1200;
/** How long a held message's menu waits for the finger to lift before taking presses anyway. */
const HOLD_ARM_MS = 1500;
/** What `.msg-in .bubble` adds on the left, past a bubble's own padding, to clear the face. */
const FACE_ROOM_PX = 12;
/** The least a held message's card keeps off the screen's edge. */
const HOLD_EDGE_PX = 8;
/** How many screens short of the top older messages are asked for. */
const PREFETCH_SCREENS = 1.5;
const VEIL_GAP = 8;

interface Props {
  uid: string;
  users: Record<string, UserDoc>;
  chat: ChatState;
  limits: Limits;
  /** A message id to open the thread around, from a search result. */
  focus?: string;
  /** Quote the focused message in the composer, not just scroll to it. */
  replyToFocus?: boolean;
  /** Asking for a profile photo, drawn under the header beside your face. */
  photoPrompt?: ReactNode;
  /** The message pinned over the thread for everyone, if one is. */
  /** Every pinned message, newest pin first. */
  pinned?: Pinned[];
  /** A streak on the line, said under the header. */
  streakNudge?: ReactNode;
}

export function Chat({
  uid,
  users,
  chat,
  limits,
  focus,
  replyToFocus,
  photoPrompt,
  pinned = [],
  streakNudge,
}: Props) {
  const live = chat;
  const around = useThreadAround(focus ?? null);
  // Reading history is a different view from the live tail, so the scrollback
  // pager is idle while one is open.
  const messages = focus ? around.messages : live.messages;
  const hasMore = focus ? false : live.hasMore;
  const loadingMore = focus ? around.loading : live.loadingMore;
  const loadOlder = focus ? () => {} : live.loadOlder;
  const [draft, setDraft] = useState(readDraft);
  const [replyTo, setReplyTo] = useState<WithId<MessageDoc> | null>(null);
  const [editing, setEditing] = useState<WithId<MessageDoc> | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Who the draft names, by uid, plus EVERYONE for @FamBam. Tracked as it is
  // typed rather than parsed back out on send, because display names contain
  // spaces and guessing where one ends is how the wrong person gets pinged.
  const [mentioned, setMentioned] = useState<Record<string, string>>({});
  const [caret, setCaret] = useState(0);
  const input = useRef<HTMLTextAreaElement>(null);

  /*
   * The family sees you typing while the draft has something in it, and it is
   * quiet exactly when what you send would be. Editing an old message is not
   * typing a new one. Reading history, the live thread is not on screen, so
   * nobody's typing is shown over it.
   */
  // A draft kept from last time is not typing: opening the app with one must
  // not tell the family "Carter is typing" until a key is actually pressed.
  const [typedHere, setTypedHere] = useState(false);
  useTypingSignal(
    uid,
    editing || !typedHere ? '' : draft,
    shouldBeQuiet(users[uid]?.email, replyTo?.data, users[uid]?.quietSending),
  );
  const typers = useWhoIsTyping(!focus, uid);
  const typingText = typingLine(typers.map((id) => users[id]?.displayName ?? 'Someone'));
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const clipInput = useRef<HTMLInputElement>(null);
  /** The composer's camera, asking photo or clip before it opens. */
  const [shooting, setShooting] = useState(false);
  const [recorder, setRecorder] = useState<Recorder | null>(null);
  const [recordedFor, setRecordedFor] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  /** The newest message the thread has already followed, so only a new one counts. */
  const newestSeen = useRef<string | undefined>(undefined);
  /** The oldest message drawn, so older ones landing above can be told apart. */
  const oldestSeen = useRef<string | undefined>(undefined);
  const dock = useRef<HTMLDivElement>(null);
  /*
   * How much room the thread leaves itself at the bottom.
   *
   * The dock floats over the scroller now, so nothing about the layout tells
   * the list how tall it is -- this measurement is what keeps the last message
   * clear of the field. It changes as the field grows, as a reply preview opens
   * and as the mention list appears, so it is measured rather than guessed.
   */
  const [dockHeight, setDockHeight] = useState(0);
  /*
   * The header floats over the top of the thread too, frosted, so the list
   * scrolls up behind it -- and leaves itself this much room at the top.
   */
  const head = useRef<HTMLDivElement>(null);
  const [headHeight, setHeadHeight] = useState(0);
  /** The newest message is somewhere below; offer the way back to it. */
  const [showJump, setShowJump] = useState(false);
  const [bounce, setBounce] = useState<'top' | 'bottom' | null>(null);
  const bounceOff = useRef<number | undefined>(undefined);
  const [polling, setPolling] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const anyFileInput = useRef<HTMLInputElement>(null);

  /*
   * Photos and clips picked from the plus or the camera, waiting in the
   * composer to go with whatever is typed. They used to post the instant they
   * were picked, so there was no way to say anything with them.
   */
  const [pending, setPending] = useState<File[]>([]);

  /** Something to send, which is what collapses the composer down to it. */
  // Editing a caption, an empty box is something to save: it takes the caption off.
  const editingCaption = editing?.data.kind === 'media';
  const typing = draft.trim() !== '' || pending.length > 0 || editingCaption;
  /*
   * The message at the top of the thread, and where it sat on screen, taken
   * just before older ones are fetched, so the reader is put back in front of
   * it once they land above.
   *
   * It held the thread's height instead and added however much it grew. On
   * Android that moved the reader twice: Chrome already holds the view in
   * place when rows arrive above it, so adding the growth again sent them a
   * whole page further down -- and a page of older messages is about as tall
   * as the thread was, so further down was the bottom. Carter, on reading
   * back: "it jumps back down to the bottom restarting my scroll". Putting a
   * message back where it was comes to nothing when the browser has already
   * done it, and to the whole correction when it has not, as on an iPhone.
   */
  const placeBeforeLoad = useRef<{ id: string; top: number } | null>(null);
  // A fetch that failed, or found nothing, moves no rows and so never spends
  // the place -- and a place still held stops the next page being asked for.
  useEffect(() => {
    if (!loadingMore) placeBeforeLoad.current = null;
  }, [loadingMore]);

  useEffect(() => {
    if (!focus || around.loading || !around.messages.length) return;
    const target = document.getElementById(`m-${focus}`);
    target?.scrollIntoView({ block: 'center' });
    target?.classList.add('found');
    return () => target?.classList.remove('found');
  }, [focus, around.loading, around.messages.length]);

  const pinToBottom = () => {
    const node = scroller.current;
    if (node) node.scrollTop = node.scrollHeight;
  };

  /** When a finger, wheel or key last moved the thread; see onScroll. */
  const lastTouched = useRef(0);
  const touched = () => {
    lastTouched.current = Date.now();
  };

  // Before the frame is painted, not after it. Read back to the very top, the
  // browser holds nothing in place, so the rows that arrived above drew for a
  // frame where the reader had been and then snapped back: Carter's "it still
  // like flashes and reloads the whole page".
  useLayoutEffect(() => {
    const node = scroller.current;
    // Noted before anything returns early, so a message that lands while older
    // ones load is not taken for a new arrival the next time round.
    const newest = messages[messages.length - 1];
    const arrived = newest?.id !== newestSeen.current;
    newestSeen.current = newest?.id;
    // Only older rows arriving above move the reader. A new message landing
    // while they are on their way used to spend the place with nothing to
    // put right, and onScroll records a place only when none is held -- so
    // when the page did land, an iPhone, which holds nothing itself, threw
    // the reader to the top of it.
    const oldest = messages[0]?.id;
    const olderArrived = oldest !== oldestSeen.current;
    oldestSeen.current = oldest;

    // Arriving from a search result positions the view itself.
    if (focus) return;

    const place = placeBeforeLoad.current;
    if (place && node && olderArrived) {
      const row = document.getElementById(place.id);
      // Moved on screen, put back in the thread's own units. The text size
      // zooms the thread, so a screen pixel is not a scroll pixel: at the
      // default size every correction came up 15% short, and back at the top
      // of a page of 100 that was fifteen messages skipped. Carter: "puts me
      // in the middle of those new messages".
      const scale = node.getBoundingClientRect().width / node.offsetWidth || 1;
      if (row) node.scrollTop += (row.getBoundingClientRect().top - place.top) / scale;
      placeBeforeLoad.current = null;
      return;
    }

    // Only follow new messages if the reader is already at the bottom;
    // otherwise scrolling back through history would yank them forward. Your
    // own always brings you down to it: you have just sent it, and it went
    // somewhere you then had to scroll to find.
    //
    // Only when it has just arrived, though. This runs whenever the count
    // moves -- a message deleted, the thread sent again on reconnecting, older
    // ones loading -- and asked only whether the newest was yours, so with
    // your message last, any of those pulled you down from wherever you were
    // reading: Carter's "randomly when I'm scrolling up".
    if (nearBottom.current || (arrived && newest?.data.authorUid === uid)) {
      nearBottom.current = true;
      pinToBottom();
    }
    // The newest message, not the count: the live page is a fixed size, so
    // once it is full a new message pushes the oldest out and the count never
    // moves -- which is what left the thread short of every new message.
  }, [messages.length, messages[messages.length - 1]?.id]);

  /**
   * Opening the tab lands on the newest message, and stays there while the
   * layout settles.
   *
   * One scroll on its own was not enough. The composer measures itself on the
   * same mount and sets its own height, and the list is what gives up the room
   * -- so the view had already been put at the bottom before the bottom moved,
   * and the thread opened a composer's height short of the end. Re-pinning on
   * any resize covers that, and the keyboard, and a late web font, without
   * needing to know which one it was.
   *
   * Only while the reader is at the bottom: the same observer must not haul
   * somebody reading last week's messages back down.
   *
   * It watched the thread's own box and nothing in it, and the box does not
   * change when what is inside it grows -- a clip's frame arriving, a late
   * font, a link's preview -- so the chat still opened short of its bottom.
   * Every row is watched now, and coming back to the app re-pins as well.
   */
  useLayoutEffect(() => {
    const node = scroller.current;
    if (!node || focus) return;

    // Back from a search result or a pin, the reader asked for the latest, so
    // the thread follows it again. Left as the focused view had it -- usually
    // reading back up -- the one pin here was the last: every message after
    // stopped short of the bottom.
    nearBottom.current = true;
    pinToBottom();

    const follow = () => {
      if (nearBottom.current) pinToBottom();
    };
    const sizes = new ResizeObserver(follow);
    const watchEach = () => {
      sizes.disconnect();
      sizes.observe(node);
      for (const child of Array.from(node.children)) sizes.observe(child);
    };
    watchEach();
    const rows = new MutationObserver(watchEach);
    rows.observe(node, { childList: true });
    const back = () => {
      if (document.visibilityState === 'visible') follow();
    };
    document.addEventListener('visibilitychange', back);
    return () => {
      sizes.disconnect();
      rows.disconnect();
      document.removeEventListener('visibilitychange', back);
    };
  }, [focus]);

  /**
   * A rubber band at either end.
   *
   * Touch already bounces natively on a phone, but a wheel or a trackpad gets
   * nothing: the list simply stops dead, which reads as the app having missed
   * the gesture. This gives the same nudge to both.
   */
  const onWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    const node = scroller.current;
    if (!node) return;

    const atTop = node.scrollTop <= 0;
    const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight <= 1;
    const edge =
      atTop && event.deltaY < 0 ? 'top' : atBottom && event.deltaY > 0 ? 'bottom' : null;
    if (!edge) return;

    setBounce(edge);
    window.clearTimeout(bounceOff.current);
    bounceOff.current = window.setTimeout(() => setBounce(null), 190);
  };

  /*
   * The box grows with what is in it, up to the six lines the stylesheet caps
   * it at, and scrolls after that.
   *
   * Measured rather than counted: a line is however much fits at this width,
   * which wrapping decides and a newline count cannot know. Reset to auto
   * first, or scrollHeight only ever reports the height it already has and the
   * box can grow but never shrink again.
   */
  useEffect(() => {
    const node = input.current;
    if (!node) return;

    node.style.height = 'auto';
    const cap = parseFloat(getComputedStyle(node).maxHeight) || Infinity;
    node.style.height = `${Math.min(node.scrollHeight, cap)}px`;

    // And the bar only once there is something under it. Left on, an empty box
    // showed a scrollbar for the fraction of a pixel it was over by.
    node.style.overflowY = node.scrollHeight > cap ? 'auto' : 'hidden';
  }, [draft]);

  // Counts up while recording, and stops the recording itself at the cap so a
  // forgotten tap cannot run for an hour.
  useEffect(() => {
    if (!recorder) return;
    const tick = setInterval(() => setRecordedFor((seconds) => seconds + 1), 1000);
    return () => clearInterval(tick);
  }, [recorder]);

  useEffect(() => {
    if (recorder && recordedFor >= limits.maxAudioSeconds) void onRecord();
  }, [recordedFor]);

  // Arriving from a daily: quote its pointer row so the reply reads as being
  // about that photo, and lands in the same thread as everything else.
  useEffect(() => {
    if (!replyToFocus || !focus) return;
    const quoted = messages.find((m) => m.id === focus);
    if (quoted) setReplyTo(quoted);
  }, [replyToFocus, focus, messages]);

  useLayoutEffect(() => {
    const node = dock.current;
    if (!node) return;
    const observer = new ResizeObserver(() => setDockHeight(node.offsetHeight));
    observer.observe(node);
    setDockHeight(node.offsetHeight);
    return () => observer.disconnect();
  }, []);

  /*
   * The dock growing used to push the list up, and the observer on the
   * scroller caught it. Out of the flow it no longer changes the scroller's
   * box at all -- only its padding -- so the re-pin has to hang off the
   * measurement instead.
   */
  useLayoutEffect(() => {
    const node = head.current;
    const list = scroller.current;
    if (!node || !list) return;
    /*
     * In the thread's own units. The header undoes the text-size zoom and the
     * thread does not, so the header's pixels are not the thread's: its height
     * on screen, divided by how much the thread is scaled on screen.
     */
    const measure = () => {
      const scale = list.offsetWidth ? list.getBoundingClientRect().width / list.offsetWidth : 1;
      setHeadHeight(node.getBoundingClientRect().height / (scale || 1));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    measure();
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (nearBottom.current && !focus) pinToBottom();
  }, [dockHeight, headHeight, focus]);

  const onScroll = () => {
    const node = scroller.current;
    if (!node) return;

    /*
     * Only a scroll you made decides whether the thread follows the bottom.
     * As the chat opens, the browser moves the view itself -- holding its
     * place while pictures and fonts arrive above -- and that read as you
     * scrolling away, so it stopped following and opened partway up. A
     * finger's fling carries on after it lifts, so each scroll inside the
     * window keeps the window open.
     */
    const now = Date.now();
    if (now - lastTouched.current < USER_SCROLL_MS) {
      lastTouched.current = now;
      nearBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 120;
    }

    /* Far enough that the way back is not just a flick. Well past the 120 that
       decides whether new messages follow, so the button is not flickering in
       and out around the same boundary. */
    setShowJump(node.scrollHeight - node.scrollTop - node.clientHeight > 400);
    markMentionsInView();

    /*
     * Fetched a screen and a half before the top, not at it. Asked for only at
     * the top, every page was something to wait for at the end of a scroll,
     * and then something that arrived while you were already up against the
     * edge. This far out they are usually in before you get there.
     *
     * Which leaves you scrolling while they come, so where the top message
     * sits is noted again on every scroll until they land. Noted once, the
     * correction would have handed you back all the reading you had done in
     * the meantime.
     */
    const pending = placeBeforeLoad.current;
    if (pending) {
      const row = document.getElementById(pending.id);
      if (row) pending.top = row.getBoundingClientRect().top;
    } else if (node.scrollTop < node.clientHeight * PREFETCH_SCREENS && hasMore && !loadingMore) {
      const first = node.querySelector<HTMLElement>('[id^="m-"]');
      placeBeforeLoad.current = first ? { id: first.id, top: first.getBoundingClientRect().top } : null;
      loadOlder();
    }
  };

  /**
   * Where the thread was left off, fixed for as long as the tab is open.
   *
   * Read while rendering on purpose. Opening the tab is also what marks it
   * read, and that happens in an effect -- so an effect here would only ever
   * find "just now" and the line would have nowhere to go. Rendering comes
   * first. Held in state rather than read again each pass so the line stays
   * put while messages arrive underneath it, and moves on the next visit,
   * which unmounts this screen and takes the value with it.
   */
  const [readLine] = useState(readChatSeenAt);

  /*
   * A deleted message is not drawn. It stays in the store, so a reply that
   * quoted it still has something to point at -- which is why `messages`, not
   * this, is what a quote is looked up in -- but a "Message deleted" row in
   * the thread told nobody anything: almost every one was a photo taken back
   * within the minute, and four of them in a row read as noise. Carter: "whats
   * the point in leaving the note in the chat".
   */
  const items = useMemo(
    () => blockSnaps(threadSnapBacks(groupDailies(messages.filter((m) => !m.data.deletedAt)))),
    [messages],
  );

  /**
   * The first message that arrived after that, if any. Nothing of one's own
   * counts, the same as the badge: a message cannot be news to whoever sent it.
   *
   * Zero means this phone has never opened the thread, and a line above the
   * whole of it says nothing worth saying.
   *
   * Searched over the rows that are actually drawn rather than over every
   * document. GiviSnap pointers are still written and chat no longer shows
   * them, so looking in `messages` could settle on one and anchor the line to
   * a row that is not there -- and a line with nothing to sit above simply
   * never appeared. One pointer is written per person per day, so most
   * mornings that was the first unread thing and the line went missing for the
   * rest of the day.
   */
  const firstUnread = useMemo(() => {
    if (!readLine || focus) return null;
    // A thread counts as new when anything in it is, since it is drawn where
    // its newest snap back is.
    const found = items.find((item) =>
      messagesOf(item).some((message) => {
        if (message.data.authorUid === uid) return false;
        const at = message.data.createdAt as { toMillis?: () => number } | null;
        return (at?.toMillis?.() ?? 0) > readLine;
      }),
    );
    return found?.message.id ?? null;
  }, [items, readLine, uid, focus]);

  /*
   * Where somebody named you since you last read, oldest first, and who did.
   *
   * WhatsApp's @: Carter asked for a way straight to the message, since a
   * question put to you by name is the thing in a busy thread you least want
   * to scroll for. A button over the composer goes to each in turn, and one
   * passed on the way down counts as seen as well, so it never offers what you
   * have already read.
   */
  const unreadMentions = useMemo(() => {
    if (!readLine || focus) return [];
    return items.flatMap((item) => {
      const naming = messagesOf(item).find((message) => mentionsMeSince(message, readLine, uid));
      return naming ? [{ id: item.message.id, by: naming.data.authorUid }] : [];
    });
  }, [items, readLine, uid, focus]);
  const [mentionsSeen, setMentionsSeen] = useState<ReadonlySet<string>>(() => new Set());
  const mentionsLeft = unreadMentions.filter((each) => !mentionsSeen.has(each.id));

  const seeMentions = (ids: string[]) => {
    if (ids.length) setMentionsSeen((seen) => new Set([...seen, ...ids]));
  };

  /** The ones on screen now, which need no button to get to. */
  const markMentionsInView = () => {
    const node = scroller.current;
    if (!node || !mentionsLeft.length) return;
    const box = node.getBoundingClientRect();
    // The header and the composer float over the thread, so a row under
    // either is inside the box and still unread. Measured on screen here
    // rather than from headHeight and dockHeight: those are in the thread's
    // units, which the text size zooms, and on opening the chat this first
    // runs before they have been measured at all.
    const top = box.top + (head.current?.getBoundingClientRect().height ?? 0);
    const bottom = box.bottom - (dock.current?.getBoundingClientRect().height ?? 0);
    seeMentions(
      mentionsLeft
        .filter(({ id }) => {
          const row = document.getElementById(`m-${id}`)?.getBoundingClientRect();
          return !!row && row.bottom > top && row.top < bottom;
        })
        .map(({ id }) => id),
    );
  };

  useEffect(markMentionsInView, [mentionsLeft.map((each) => each.id).join()]);

  const openMention = () => {
    const next = mentionsLeft[0];
    if (!next) return;
    seeMentions([next.id]);
    openPinned(next.id);
  };
  const active = activeMention(draft, caret);

  /** @FamBam first, then whoever matches what has been typed so far. */
  const candidates: Array<{ key: string; name: string; hint: string }> = active
    ? [
        { key: EVERYONE, name: CHAT_NAME, hint: 'Everyone' },
        ...Object.entries(users)
          .filter(([memberUid]) => memberUid !== uid)
          .map(([memberUid, member]) => ({
            key: memberUid,
            name: member.displayName,
            hint: '',
          })),
      ].filter(({ name }) => name.toLowerCase().startsWith(active.query.toLowerCase()))
    : [];

  const insertMention = (key: string, name: string) => {
    if (!active) return;
    const before = draft.slice(0, active.start);
    const next = `${before}@${name} ${draft.slice(caret)}`;
    const at = before.length + name.length + 2;

    setDraft(next);
    setMentioned((current) => ({ ...current, [key]: name }));
    // After React has written the new value, or the caret jumps to the end.
    requestAnimationFrame(() => {
      input.current?.focus();
      input.current?.setSelectionRange(at, at);
      setCaret(at);
    });
  };

  /**
   * Only names still present in the text count, so deleting a mention unsends
   * its notification. @FamBam opens out to the whole family bar the author.
   */
  const mentionsIn = (text: string): string[] => {
    const named = Object.entries(mentioned).filter(([, name]) => text.includes(`@${name}`));
    if (named.some(([key]) => key === EVERYONE)) {
      return Object.keys(users).filter((memberUid) => memberUid !== uid);
    }
    return named.map(([key]) => key);
  };

  const startEdit = (message: WithId<MessageDoc>) => {
    setReplyTo(null);
    setEditing(message);
    setDraft(message.data.text ?? '');
    input.current?.focus();
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(readDraft());
  };

  /*
   * What is being typed outlives the app: the phone closing it, a reload, a
   * switch to another app that it never comes back from. Not while an old
   * message is being edited -- that is not a draft -- and the draft from
   * before the edit comes back when the edit is done.
   */
  useEffect(() => {
    if (!editing) writeDraft(draft);
  }, [draft, editing]);

  /*
   * The pin is everybody's, so anybody may set it -- except the test account,
   * which the family never sees and so must never put anything over them.
   */
  const canPin = !isQuietSender(users[uid]?.email);
  /** The message Pin was pressed on, while the sheet asks whether it is an event. */
  const [pinning, setPinning] = useState<WithId<MessageDoc> | null>(null);
  /** The list of everything pinned, open over the chat. */
  const [listingPins, setListingPins] = useState(false);
  const today = dayKey(new Date());
  const pin = (message: WithId<MessageDoc>) => setPinning(message);
  const pinAs = (message: WithId<MessageDoc>, event?: PinEvent) => {
    setPinning(null);
    void pinMessage(message.id, uid, event).catch(() => setError('That did not pin. Try again.'));
  };
  const unpin = (pinId: string) =>
    unpinMessage(pinId).catch(() => setError('That did not unpin. Try again.'));
  /** The pin on this message, if it has one. */
  const pinOn = (messageId: string) => pinned.find((each) => each.message.id === messageId);

  /**
   * To the pinned message: where it is in the thread, if it is loaded, and the
   * thread around it if it is older than that.
   */
  const openPinned = (id: string) => {
    const row = document.getElementById(`m-${id}`);
    if (!row) {
      go(`chat/${id}`);
      return;
    }
    // Reading back up now, so the thread stops following its bottom.
    nearBottom.current = false;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    row.classList.add('found');
    window.setTimeout(() => row.classList.remove('found'), 1600);
  };

  const onDelete = async (message: WithId<MessageDoc>) => {
    // Irreversible, and it takes any photo in R2 with it, so it asks first.
    if (!window.confirm('Delete this message? Everyone will see that you did.')) return;
    try {
      await deletePost({ target: { kind: 'message', id: message.id } });
    } catch {
      setError('That did not delete. Try again in a moment.');
    }
  };

  const onSend = async () => {
    const text = draft.trim();
    if (!text && pending.length === 0 && !editingCaption) return;

    // Sending is the moment you want to see it: back to the foot of the chat.
    nearBottom.current = true;
    pinToBottom();

    if (pending.length > 0 && !editing) {
      sendPending(text);
      return;
    }
    if (!text && !editingCaption) return;

    if (editing) {
      const target = editing;
      setEditing(null);
      setDraft(readDraft());
      try {
        await editMessage(target.id, text);
      } catch {
        setEditing(target);
        setDraft(text);
        setError('That edit did not save. Still here when you try again.');
      }
      return;
    }

    const quoted = replyTo?.id;
    const mentions = mentionsIn(text);
    setDraft('');
    setReplyTo(null);
    setMentioned({});
    try {
      await sendText(uid, text, mentions, quoted);
    } catch {
      // Put the draft, the quote and the mentions back rather than losing any.
      setDraft(text);
      if (replyTo) setReplyTo(replyTo);
      setMentioned(mentioned);
      setError('That did not send. Still here when you try again.');
    }
  };

  /**
   * Recording runs from a tap rather than a press-and-hold: a hold competes
   * with the swipe-to-reply gesture already on this screen, and two minutes is
   * a long time to keep a thumb down.
   */
  const onRecord = async () => {
    if (recorder) {
      const recording = await recorder.stop();
      setRecorder(null);
      setRecordedFor(0);
      if (!recording) return;
      if (recording.durationSec > limits.maxAudioSeconds) {
        setError(`Voice notes are capped at ${Math.round(limits.maxAudioSeconds / 60)} minutes.`);
        return;
      }
      const file = new File([recording.blob], 'voice', { type: recording.blob.type });
      enqueueUpload({
        kind: 'chat-voice',
        file,
        durationSec: recording.durationSec,
        target: { kind: 'message', id: crypto.randomUUID() },
      });
      return;
    }

    setError(null);
    try {
      setRecorder(await startRecording());
    } catch {
      setError('The microphone is not available. Check the permission and try again.');
    }
  };

  /**
   * What is waiting in the composer, sent with the words typed beside it.
   *
   * The photos go as one message, the way they were picked together, with the
   * words as its caption; each clip keeps a message to itself, as it always
   * has, and carries the words only when there is no photo to carry them.
   *
   * A quote or an @ is something a photo message cannot hold yet, so words
   * with either go as a message of their own straight after, rather than
   * losing the quote or failing to tell the person named.
   */
  const sendPending = (text: string) => {
    const files = pending;
    const quoted = replyTo?.id;
    const mentions = mentionsIn(text);
    const asCaption = text && !quoted && mentions.length === 0 ? text.slice(0, MESSAGE_TEXT_MAX) : '';

    setPending([]);
    setDraft('');
    setReplyTo(null);
    setMentioned({});

    const photos = files.filter((file) => !file.type.startsWith('video/'));
    const videos = files.filter((file) => file.type.startsWith('video/'));

    // Everything picked in one go shares a target id, and attachMedia unions
    // each one onto that document, so a handful of photos arrives as the one
    // message it was sent as rather than a run of near-identical ones.
    if (photos.length > 0) {
      const target = {
        kind: 'message' as const,
        id: crypto.randomUUID(),
        ...(asCaption ? { text: asCaption } : {}),
      };
      for (const file of photos) enqueueUpload({ kind: 'chat-photo', file, target });
    }
    // A clip keeps a message to itself: it appears in a processing state and
    // carries a poster, neither of which belongs in a grid of photos.
    videos.forEach((file, i) => {
      const words = asCaption && photos.length === 0 && i === 0 ? { text: asCaption } : {};
      enqueueUpload({
        kind: 'chat-video',
        file,
        target: { kind: 'message', id: crypto.randomUUID(), ...words },
      });
    });

    if (text && !asCaption) {
      void sendText(uid, text, mentions, quoted).catch(() => {
        setDraft(text);
        setError('Your photos went, but the words did not. Still here when you try again.');
      });
    }
  };

  /**
   * Photos and clips from the plus or the camera, into the composer to wait
   * for Send. A clip is checked here, before it is anything you could send,
   * so a too-long one is turned away while you are still looking at it.
   */
  const onPick = async (picked: FileList | File[] | null) => {
    const all = Array.from(picked ?? []);
    if (all.length === 0) return;
    setError(null);

    /*
     * The camera button's chooser is unrestricted, so that the camera apps
     * appear in it at all, which means a document can come back from it as
     * easily as a photograph. The library input beside it is still narrowed by
     * `accept` and never sends anything else here.
     */
    const files = all.filter(
      (file) => file.type.startsWith('image/') || file.type.startsWith('video/'),
    );
    if (files.length === 0) {
      setError('That is not a photo or a clip.');
      return;
    }

    const ready: File[] = [];
    for (const file of files) {
      if (!file.type.startsWith('video/')) {
        ready.push(file);
        continue;
      }
      // Check duration and size before spending bandwidth. createUpload
      // checks again server-side, since these numbers come from the client.
      try {
        const facts = await inspectVideo(file);
        const verdict = checkVideo(file, facts, limits);
        if (!verdict.ok) {
          setError(verdict.reason ?? 'That clip will not work.');
          continue;
        }
      } catch {
        setError('That video could not be read.');
        continue;
      }
      ready.push(file);
    }
    if (ready.length === 0) return;
    setPending((waiting) => [...waiting, ...ready]);
    input.current?.focus();
  };

  /*
   * Shared in from another app's Share menu: into the composer on opening, as
   * if picked from the plus -- through the same checks on a clip -- with any
   * words shared alongside to start the message.
   */
  useEffect(() => {
    void takeShared()
      .then((shared) => {
        if (!shared) return;
        if (shared.text) setDraft((typed) => (typed ? `${typed} ${shared.text}` : shared.text));
        if (shared.files.length) void onPick(shared.files);
      })
      .catch(() => undefined);
  }, []);

  /**
   * Anything that is not a photo, a clip or a recording. It is never drawn,
   * only offered back as a download, so there is nothing to inspect first.
   */
  const onAttach = async (file: File | undefined) => {
    if (!file) return;
    setError(null);

    if (file.size > limits.maxFileBytes) {
      setError(`That file is over the ${Math.round(limits.maxFileBytes / 1e6)} MB limit.`);
      return;
    }

    enqueueUpload({
      kind: 'chat-file',
      file,
      target: { kind: 'message', id: crypto.randomUUID() },
    });
  };

  /**
   * Where you are, as two numbers.
   *
   * No map is stored and no key is needed for one: the row opens whatever the
   * phone uses for maps, which is the thing anybody receiving it was going to
   * do with it anyway.
   */
  const onShareLocation = async () => {
    setError(null);
    if (!navigator.geolocation) {
      setError('This browser will not say where it is.');
      return;
    }

    try {
      const at = await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10_000,
        }),
      );
      await sendLocation(at.coords.latitude, at.coords.longitude);
    } catch {
      setError('Could not get your location. Check the permission and try again.');
    }
  };

  return (
    <>
      {/*
        The header and the banners under it float over the thread together.
        The header alone used to float, and the banners -- a pinned message,
        the offline note, the photo prompt, the streak nudge -- sat in the
        page under it, hidden by the glass, so a pinned message could not be
        seen at all. Measured as one, the thread leaves room for all of it.
      */}
      <div className="chat-top" ref={head}>
      <div className="header row chat-header" style={{ gap: 8 }}>
        {/* Your profile, from the corner: it left the tab bar when the chat
            became the middle of the app. */}
        <button className="icon-btn header-me" aria-label="Your profile" onClick={() => go('you')}>
          <Avatar
            name={users[uid]?.displayName ?? 'You'}
            photoURL={avatarOf(users[uid])}
            size={34}
          />
        </button>
        <span className="col grow" style={{ alignItems: 'flex-start', gap: 4 }}>
          <span style={{ fontSize: 19, fontWeight: 800 }}>{CHAT_NAME}</span>
          <span className="tiny muted">
            {Object.keys(users).length} people &middot; kept for good
          </span>
        </span>
        <button
          className="icon-btn"
          aria-label="Search messages"
          onClick={() => go('search')}
        >
          <IconSearch size={20} />
        </button>
        <button className="icon-btn" aria-label="Open the gallery" onClick={() => go('gallery')}>
          <IconImage size={20} />
        </button>
      </div>
      <OfflineNote />
      {photoPrompt}
      {streakNudge}
      {pinned.length > 0 && (
        <PinnedBar
          pins={pinned}
          users={users}
          today={today}
          onOpen={openPinned}
          onList={() => setListingPins(true)}
          onUnpin={canPin ? (pinId) => void unpin(pinId) : undefined}
        />
      )}
      </div>
      {pinning && (
        <PinSheet
          message={pinning}
          users={users}
          today={today}
          onPin={(event) => pinAs(pinning, event)}
          onClose={() => setPinning(null)}
        />
      )}
      {listingPins && pinned.length > 0 && (
        <PinList
          pins={pinned}
          users={users}
          today={today}
          onOpen={openPinned}
          onUnpin={canPin ? (pinId) => void unpin(pinId) : undefined}
          onClose={() => setListingPins(false)}
        />
      )}

      {focus && (
        // A swap, not a step: back from the latest should leave the chat, not
        // return to the message you just asked to leave.
        <button className="jump-back" onClick={() => swap('chat')}>
          {around.found ? 'Back to the latest' : 'That message is gone — back to the latest'}
        </button>
      )}

      <div
        className={['scroll pad col chat-thread', bounce ? `bounce-${bounce}` : ''].join(' ').trim()}
        ref={scroller}
        onScroll={onScroll}
        onWheel={(event) => {
          touched();
          onWheel(event);
        }}
        onTouchStart={touched}
        onTouchMove={touched}
        onTouchEnd={touched}
        onPointerDown={touched}
        onKeyDown={touched}
        style={{
          gap: 8,
          paddingTop: headHeight + 6,
          scrollPaddingTop: headHeight,
          paddingBottom: dockHeight + VEIL_REACH + VEIL_GAP,
        }}
      >
        {/* Up there for as long as there is more behind it, rather than only
            while a page is coming: put in and taken out, it moved the thread
            under the reader each time. Reaching it is the sign more are on
            the way, and pulling past it stretches as the phone always does. */}
        {(hasMore || loadingMore) && (
          <div className="row center" style={{ gap: 10, padding: '8px 0' }}>
            <Spinner />
            <span className="tiny muted">Loading older messages</span>
          </div>
        )}
        {!hasMore && messages.length > 0 && (
          <span className="tiny dim" style={{ textAlign: 'center', padding: '6px 0' }}>
            That&rsquo;s the start of the thread
          </span>
        )}
        {items.map((item, index) => {
          const previous = items[index - 1];
          const marker = timeMarkerFor(previous && itemAt(previous), itemAt(item));
          const opensUnread = firstUnread !== null && item.message.id === firstUnread;

          return (
            <Fragment key={item.message.id}>
              {opensUnread && (
                <div className="unread-line" role="separator">
                  <span>New messages</span>
                </div>
              )}
              {marker && <span className="time-marker">{marker}</span>}
              {item.block ? (
                // Snaps on their own, side by side: one chip a person.
                <SnapChips
                  snaps={item.block}
                  uid={uid}
                  users={users}
                  onDelete={(snap) => void onDelete(snap)}
                />
              ) : item.thread ? (
                // A snap with snap backs: the exchange, as one row.
                <SnapConversation
                  snaps={messagesOf(item)}
                  uid={uid}
                  users={users}
                  onDelete={(snap) => void onDelete(snap)}
                />
              ) : (
                <Row
                  message={item.message}
                  uid={uid}
                  users={users}
                  parent={
                    item.message.data.replyTo
                      ? messages.find((m) => m.id === item.message.data.replyTo)
                      : undefined
                  }
                  onReply={() => setReplyTo(item.message)}
                  onEdit={() => startEdit(item.message)}
                  onDelete={() => void onDelete(item.message)}
                  pinnedHere={Boolean(pinOn(item.message.id))}
                  onPin={canPin ? () => pin(item.message) : undefined}
                  onUnpin={
                    canPin
                      ? () => {
                          const here = pinOn(item.message.id);
                          if (here) void unpin(here.id);
                        }
                      : undefined
                  }
                />
              )}
            </Fragment>
          );
        })}
      </div>

      {error && (
        <div className="pad" style={{ paddingBottom: 8 }}>
          <span className="small" style={{ color: 'var(--danger)' }}>
            {error}
          </span>
        </div>
      )}

      {editing && (
        <div className="compose-banner">
          <span
            style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: 'var(--accent)' }}
          />
          <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
            <span className="tiny bold" style={{ color: 'var(--accent)' }}>
              Editing
            </span>
            <span className="tiny muted truncate">{editing.data.text}</span>
          </span>
          <button
            className="icon-btn"
            aria-label="Cancel edit"
            onClick={cancelEdit}
            style={{ width: 34, height: 34 }}
          >
            <IconClose size={16} />
          </button>
        </div>
      )}

      <div className="composer-dock" ref={dock}>
        {mentionsLeft.length > 0 && (
          <button
            type="button"
            className={showJump ? 'jump-mention is-stacked' : 'jump-mention'}
            aria-label={
              mentionsLeft.length === 1
                ? `Go to where ${users[mentionsLeft[0].by]?.displayName ?? 'someone'} mentioned you`
                : `Go to the first of ${mentionsLeft.length} mentions of you`
            }
            onClick={openMention}
          >
            <span aria-hidden="true">@</span>
            {mentionsLeft.length === 1
              ? `${users[mentionsLeft[0].by]?.displayName ?? 'Someone'} mentioned you`
              : `${mentionsLeft.length} mentions`}
          </button>
        )}
        {showJump && (
          <button
            className="jump-bottom"
            aria-label="Jump to the newest message"
            onClick={() => {
              // Reading history, the list holds only the few dozen messages
              // around the one that was opened, so its bottom is years short
              // of the newest. Leave for the live thread, which lands at its
              // own bottom when focus clears.
              if (focus) {
                swap('chat');
                return;
              }
              // Asked to go to the bottom, so it follows the bottom from here.
              nearBottom.current = true;
              scroller.current?.scrollTo({
                top: scroller.current.scrollHeight,
                behavior: 'smooth',
              });
            }}
          >
            <IconDown size={20} />
          </button>
        )}

        {/* Always there, so a screen reader announces the line as it changes. */}
        <div className="typing-line" aria-live="polite">
          {typingText && (
            <>
              <span className="typing-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              {typingText}
            </>
          )}
        </div>

        {/*
          Said where it matters, every time: with quiet sending on, nothing
          typed here reaches the family, and forgetting that is how a real
          message goes nowhere.
        */}
        {users[uid]?.quietSending && (
          <div className="quiet-banner" role="status">
            Sending quietly · only the test accounts see this
          </div>
        )}

        {replyTo && (
          <div className="compose-banner">
            <span
              style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: 'var(--accent)' }}
            />
            <QuoteThumb message={replyTo} size={32} />
            <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
              <span className="tiny bold" style={{ color: 'var(--accent)' }}>
                Replying to {replyTo.data.authorUid === uid ? 'yourself' : (users[replyTo.data.authorUid]?.displayName ?? 'someone')}
              </span>
              <span className="tiny muted truncate">{quoteOf(replyTo)}</span>
            </span>
            <button
              className="icon-btn"
              aria-label="Cancel reply"
              onClick={() => setReplyTo(null)}
              style={{ width: 34, height: 34 }}
            >
              <IconClose size={16} />
            </button>
          </div>
        )}

        {candidates.length > 0 && (
          <div className="mention-picker" role="listbox" aria-label="Mention someone">
            {candidates.map(({ key, name, hint }) => (
              <button
                key={key}
                role="option"
                aria-selected="false"
                className="mention-option"
                onClick={() => insertMention(key, name)}
              >
                {key === EVERYONE ? (
                  <span className="mention-everyone" aria-hidden="true">
                    @
                  </span>
                ) : (
                  <Avatar
                    name={name}
                    photoURL={avatarOf(users[key])}
                    size={28}
                  />
                )}
                <span className="grow bold" style={{ fontSize: 15 }}>
                  {name}
                </span>
                {hint && <span className="tiny muted">{hint}</span>}
              </button>
            ))}
          </div>
        )}

        {recorder && (
          <div className="recording-bar">
            <span className="recording-dot" aria-hidden="true" />
            <span className="grow small">
              Recording {Math.floor(recordedFor / 60)}:
              {String(recordedFor % 60).padStart(2, '0')}
            </span>
            <button
              className="tiny bold"
              onClick={() => {
                recorder.cancel();
                setRecorder(null);
                setRecordedFor(0);
              }}
              style={{ background: 'none', border: 0, color: 'var(--danger)' }}
            >
              Discard
            </button>
          </div>
        )}

        {pending.length > 0 && (
          <div className="composer-pending" role="list" aria-label="Waiting to send">
            {pending.map((file, i) => (
              <PendingThumb
                key={`${file.name}-${file.size}-${file.lastModified}-${i}`}
                file={file}
                onRemove={() => setPending((waiting) => waiting.filter((_, at) => at !== i))}
              />
            ))}
          </div>
        )}

        <div className="composer">
          <input
            ref={fileInput}
            type="file"
            accept="image/*,video/*"
            multiple
            className="sr-only"
            onChange={(e) => {
              void onPick(e.target.files);
              // Cleared so picking the same photo twice running still fires.
              e.target.value = '';
            }}
          />
          {/* `capture` sends this straight to the phone's own camera rather than
              its library, which is the difference between two taps and five.
              The chooser that briefly replaced it reached the camera app and
              gained nothing: see the camera screen's shutters. */}
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(e) => {
              void onPick(e.target.files);
              e.target.value = '';
            }}
          />
          {/* A clip for the chat, from the phone's camera. Its own input: Android
              opens the camera only for an input that accepts one kind. */}
          <input
            ref={clipInput}
            type="file"
            accept="video/*"
            capture
            className="sr-only"
            onChange={(e) => {
              void onPick(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={anyFileInput}
            type="file"
            className="sr-only"
            onChange={(e) => void onAttach(e.target.files?.[0])}
          />

          <button
            className="icon-btn composer-btn"
            aria-label="Attach something"
            aria-expanded={attaching}
            onClick={() => setAttaching((open) => !open)}
          >
            <IconPlus size={19} />
          </button>

          {/*
            * A photo or a clip to keep, from the camera. Snaps have the camera
            * key in the bar below, so this one is for the chat's own pictures,
            * which stay. It steps aside once there is something typed, where it
            * would only be competing for the room the words want.
            */}
          {!typing && (
            <button
              className="icon-btn composer-btn"
              aria-label="Take a photo or video"
              aria-haspopup="dialog"
              onClick={() => setShooting(true)}
            >
              <IconCamera size={19} />
            </button>
          )}

          <label className="sr-only" htmlFor="chat-input">
            Message {CHAT_NAME}
          </label>
          {/*
            * A textarea, so a long message is readable while it is being
            * written. It grows to six lines and then scrolls, which is the
            * point at which a composer would otherwise be eating the thread it
            * is part of.
            */}
          <textarea
            id="chat-input"
            ref={input}
            className="composer-input"
            rows={1}
            value={draft}
            placeholder={`Message ${CHAT_NAME}`}
            onChange={(e) => {
              setTypedHere(true);
              setDraft(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
            onKeyDown={(e) => {
              // Enter sends where there is a keyboard with a shift key to hold
              // for a newline. On a phone it makes a newline and the button
              // sends, because there is nowhere to put the other gesture.
              if (e.key === 'Enter' && !e.shiftKey && !isTouch()) {
                e.preventDefault();
                void onSend();
              }
            }}
          />
          {/*
            * The microphone stands beside send rather than in its place.
            *
            * It used to take the same slot whenever the box was empty, which is
            * exactly when somebody goes looking for the send button — so to
            * anyone who had not typed yet there was no send button in this app,
            * and the answer "it appears once you type" is not one you can read
            * off the screen.
            *
            * It steps aside once there is something to send, where it would only
            * be competing for the same tap.
            */}
          {!typing && canRecord() && (
            <button
              className="icon-btn composer-btn"
              aria-label={recorder ? 'Stop and send the voice note' : 'Record a voice note'}
              onClick={() => void onRecord()}
              style={
                recorder
                  ? { background: 'var(--danger)', color: 'var(--accent-ink)' }
                  : { background: 'var(--accent)', color: 'var(--accent-glyph)' }
              }
            >
              <IconMic size={19} />
            </button>
          )}

          {/*
            * Only once there is something to send. It used to sit there greyed
            * out, which is a button asking to be pressed and refusing -- and it
            * is the other half of the microphone above, which steps aside the
            * moment you start typing. One or the other, never both and never a
            * dead one.
            */}
          {typing && (
            <button
              className="icon-btn composer-btn"
              aria-label="Send"
              onClick={() => void onSend()}
              style={{ background: 'var(--accent)', color: 'var(--accent-glyph)' }}
            >
              <IconSend size={19} />
            </button>
          )}
        </div>
      </div>

      {/*
        * What the plus offers. A sheet rather than a popover: it is four
        * choices on a phone, and a popover that small is a thing you miss.
        */}
      {attaching && (
        <Sheet title="Send something" onClose={() => setAttaching(false)}>
          {[
            {
              label: 'Photo or video',
              hint: 'From your library',
              icon: <IconImage size={19} />,
              go: () => fileInput.current?.click(),
            },
            {
              label: 'Take a photo',
              hint: 'Kept in the chat for good',
              icon: <IconCamera size={19} />,
              go: () => cameraInput.current?.click(),
            },
            {
              label: 'File',
              hint: 'Anything else',
              icon: <IconFile size={19} />,
              go: () => anyFileInput.current?.click(),
            },
            {
              label: 'Poll',
              hint: 'Ask the family',
              icon: <IconPoll size={19} />,
              go: () => setPolling(true),
            },
            {
              label: 'Location',
              hint: 'Where you are now',
              icon: <IconPin size={19} />,
              go: () => void onShareLocation(),
            },
          ].map((each) => (
            <button
              key={each.label}
              className="attach-row"
              onClick={() => {
                setAttaching(false);
                each.go();
              }}
            >
              <span className="attach-icon">{each.icon}</span>
              <span className="col grow" style={{ gap: 2, alignItems: 'flex-start' }}>
                <span className="bold" style={{ fontSize: 15 }}>
                  {each.label}
                </span>
                <span className="tiny dim">{each.hint}</span>
              </span>
            </button>
          ))}
        </Sheet>
      )}

      {/*
        * Photo or clip, asked first: Android opens its camera only for an
        * input that wants one kind. Each opens straight from the tap, the only
        * moment a browser lets a page open the camera. Or one already taken,
        * from the library -- the same picker as the attach menu's.
        */}
      {shooting && (
        <Sheet title="Photo or video to keep" onClose={() => setShooting(false)}>
          {[
            { label: 'Photo', icon: <IconCamera size={19} />, input: cameraInput },
            { label: 'Video', icon: <IconVideo size={19} />, input: clipInput },
            { label: 'From your photos', icon: <IconImage size={19} />, input: fileInput },
          ].map((each) => (
            <button
              key={each.label}
              className="attach-row"
              onClick={() => {
                setShooting(false);
                each.input.current?.click();
              }}
            >
              <span className="attach-icon">{each.icon}</span>
              <span className="col grow" style={{ gap: 2, alignItems: 'flex-start' }}>
                <span className="bold" style={{ fontSize: 15 }}>
                  {each.label}
                </span>
                <span className="tiny dim">Stays in {CHAT_NAME} for good</span>
              </span>
            </button>
          ))}
          {/* Which camera is which: this one keeps, the key below snaps. */}
          <span className="tiny dim" style={{ textAlign: 'center' }}>
            For a snap everyone sees once, use Snap below.
          </span>
        </Sheet>
      )}

      {polling && <PollSheet onClose={() => setPolling(false)} />}
    </>
  );
}

/** One line of context for a quoted message, whatever kind it is. */
type Gesture = { x: number; y: number; timer: number; swiping: boolean };

/**
 * A run of dailies is one row, not one row each.
 *
 * The prompt fires and all eleven post within a few minutes of each other, so
 * a row apiece would push the conversation off the screen on the one day
 * everybody turns up. Anything else breaks the run, so a straggler an hour
 * later still gets a line of their own.
 */
/** When an item happened, for ordering and time markers. */
function itemAt(item: Item): unknown {
  return latestOf(item).data.createdAt;
}


/**
 * One line and a face or three, with no card around it: a daily is something
 * that happened, not something said. Tapping opens the day in the pager — one
 * person's, or the whole day when several posted together.
 */


/** One chip per distinct emoji, most used first. Someone may add several. */
function tallyReactions(
  likes: MessageDoc['likes'] | undefined,
): { emoji: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const value of Object.values(likes ?? {})) {
    for (const emoji of emojisOf(value)) {
      counts.set(emoji, (counts.get(emoji) ?? 0) + 1);
    }
  }
  return [...counts]
    .map(([emoji, count]) => ({ emoji, count }))
    .sort((a, b) => b.count - a.count);
}

/** What a message is, as a quote: shared/summary.ts, which the notifications use too. */
function quoteOf(message: WithId<MessageDoc>): string {
  return summaryOf(message.data);
}

/**
 * What is pinned over the thread, for the whole family: the soonest event to
 * come, with its day and how far off it is -- or, with no events, the newest
 * plain pin, who said it and the start of what. A tap goes to its message.
 * With more than one pinned, a count opens them all (PinList); with one, a
 * cross takes it down.
 */
function PinnedBar({
  pins,
  users,
  today,
  onOpen,
  onList,
  onUnpin,
}: {
  pins: Pinned[];
  users: Record<string, UserDoc>;
  today: string;
  onOpen: (messageId: string) => void;
  onList: () => void;
  onUnpin?: (pinId: string) => void;
}) {
  const first = pins[0];
  const event = first.event;
  const who = users[first.message.data.authorUid]?.displayName ?? 'Someone';
  const what = quoteOf(first.message) || 'a message';
  const label = event ? `Pinned: ${event.name}, ${whenWords(event, today)}` : `Pinned: ${who}, ${what}`;
  return (
    <div className="pinned-bar">
      <button type="button" className="pinned-open" onClick={() => onOpen(first.message.id)} aria-label={label}>
        {event ? (
          <>
            <EventTile day={event.day} />
            <span className="pinned-event">
              <span className="pinned-event-name">{event.name}</span>
              <span className="pinned-event-when">{whenWords(event, today)}</span>
            </span>
          </>
        ) : (
          <>
            <span className="pinned-mark" aria-hidden="true">
              <IconPin size={14} />
            </span>
            <QuoteThumb message={first.message} size={28} />
            <span className="pinned-text">
              <span className="pinned-who">{who}</span> {what}
            </span>
          </>
        )}
      </button>
      {pins.length > 1 ? (
        <button type="button" className="pinned-count" aria-label={`All ${pins.length} pinned`} onClick={onList}>
          {pins.length}
          <IconForward size={14} />
        </button>
      ) : (
        onUnpin && (
          <button type="button" className="pinned-close" aria-label="Unpin" onClick={() => onUnpin(first.id)}>
            <IconClose size={14} />
          </button>
        )
      )}
    </div>
  );
}

/**
 * A thumbnail that asks again once if it fails.
 *
 * After a reload some thumbnails came up as broken pictures while tapping
 * them still opened the photo: the first ask had failed and an <img> never
 * asks twice. So a failure gets a fresh media cookie and one more go, drawn
 * as a new <img> so the browser really does ask again.
 */
function RetryingImg(props: ComponentProps<typeof Photo>) {
  const [attempt, setAttempt] = useState(0);
  return (
    <Photo
      key={attempt}
      {...props}
      onError={() => {
        if (attempt > 0) return;
        void renewMediaSession()
          .catch(() => undefined)
          .finally(() => setAttempt(1));
      }}
    />
  );
}

/**
 * One photo or clip waiting in the composer, with a way to take it back out.
 * The preview is a url for the file itself, let go of once it is gone.
 */
function PendingThumb({ file, onRemove }: { file: File; onRemove: () => void }) {
  const video = file.type.startsWith('video/');
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (video) return;
    const made = URL.createObjectURL(file);
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [file, video]);

  return (
    <span className="composer-pending-item" role="listitem">
      {url ? (
        <img src={url} alt="" />
      ) : (
        <span className="composer-pending-clip" aria-hidden="true">
          <IconPlay size={18} />
        </span>
      )}
      <button
        type="button"
        className="composer-pending-remove"
        aria-label={video ? 'Take out this clip' : 'Take out this photo'}
        onClick={onRemove}
      >
        <IconClose size={12} />
      </button>
    </span>
  );
}

function Row({
  message,
  uid,
  users,
  parent,
  onReply,
  onEdit,
  onDelete,
  pinnedHere = false,
  onPin,
  onUnpin,
}: {
  message: WithId<MessageDoc>;
  uid: string;
  users: Record<string, UserDoc>;
  parent?: WithId<MessageDoc>;
  onReply?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Whether this is the message pinned over the thread. */
  pinnedHere?: boolean;
  /** Absent for anyone who may not pin -- the test account. */
  onPin?: () => void;
  onUnpin?: () => void;
}) {
  const data = message.data;
  // Declared above the system and pointer rows below, which return early.
  const [picker, setPicker] = useState(false);
  /*
   * Whether the finger that held the message has been lifted. The lifted
   * message's menu opens under that finger, still down, and until it comes
   * up the menu takes nothing from it: on Android it was a long press on
   * whichever word was under the thumb -- usually Pin, in the middle of the
   * screen -- which the phone selected and covered with its own popup.
   */
  const [armed, setArmed] = useState(false);
  /*
   * How wide the message is in the thread, so the lifted copy wraps exactly
   * as it did there. Off the thread a bubble takes a narrower default, and
   * the words that came up were laid out differently from the ones held --
   * which read as something not lining up. Somebody else's has room in the
   * thread for their face, which the copy does not draw.
   */
  const [liftedWidth, setLiftedWidth] = useState<number | null>(null);
  /*
   * And where its edge was on the screen -- the right one for yours, the
   * left for anybody else's -- so the lifted message, its reactions and its
   * menu all line up on the spot it came from, at any text size. A margin of
   * the layer's own matched the thread's at one size and missed it at the
   * others.
   */
  const [liftedEdge, setLiftedEdge] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!picker) return;
    const shown = held.current?.firstElementChild as HTMLElement | null;
    setLiftedWidth(shown ? shown.offsetWidth - (mine ? 0 : FACE_ROOM_PX) : null);
    const box = shown?.getBoundingClientRect();
    setLiftedEdge(box ? (mine ? window.innerWidth - box.right : box.left) : null);
  }, [picker]);
  useEffect(() => {
    if (!picker) {
      setArmed(false);
      return;
    }
    const arm = () => setArmed(true);
    window.addEventListener('pointerup', arm, true);
    window.addEventListener('pointercancel', arm, true);
    // Opened by something other than a finger still down, it still works.
    const anyway = window.setTimeout(arm, HOLD_ARM_MS);
    return () => {
      window.removeEventListener('pointerup', arm, true);
      window.removeEventListener('pointercancel', arm, true);
      window.clearTimeout(anyway);
    };
  }, [picker]);
  /*
   * The full emoji panel is open over the bar.
   *
   * It has to suspend the dismissal below. Focusing the search field makes the
   * browser scroll it into view, that scroll is not inside the bar, and the
   * bar took it for a tap elsewhere and closed -- so the panel shut the
   * instant it was touched.
   */
  const [picking, setPicking] = useState(false);
  const [drag, setDrag] = useState(0);
  const [showTime, setShowTime] = useState(false);
  const gesture = useRef<Gesture | null>(null);
  const dragged = useRef(0);
  const longFired = useRef(false);
  /**
   * Which photo the pointer came up on, recorded by the tile a moment before
   * the row's own handler runs, because pointer events reach the tile first.
   * The row is the only place that knows whether this was a tap at all.
   */
  const tappedPhoto = useRef<number | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const held = useRef<HTMLDivElement>(null);

  /*
   * Picking out part of the words, on a phone. A long press is the reaction
   * bar; trying to make it start the phone's selection as well put the two
   * on top of each other. Select, beside Copy, turns the words selectable
   * where they sit and selects them all, so the phone's handles are there to
   * drag in to the part wanted and its own Copy takes it. While it is on the
   * message is still -- no swipe, no bar, no time -- and a press anywhere
   * else ends it.
   */
  const [selecting, setSelecting] = useState(false);
  useEffect(() => {
    if (!selecting || !held.current) return;
    const words = held.current.querySelector('.bubble');
    if (words) {
      const range = document.createRange();
      range.selectNodeContents(words);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    const leave = (event: Event) => {
      if (held.current?.contains(event.target as Node)) return;
      setSelecting(false);
      window.getSelection()?.removeAllRanges();
    };
    // Deferred, or the tap on Select that started this would end it.
    const armed = window.setTimeout(() => document.addEventListener('pointerdown', leave), 0);
    return () => {
      window.clearTimeout(armed);
      document.removeEventListener('pointerdown', leave);
    };
  }, [selecting]);

  useBackCloses(() => setPicker(false), picker);

  /*
   * A tap on the blur round a lifted message puts it back (the scrim's own
   * click, below), and so does a scroll.
   *
   * It used to go on any press outside the bar, the moment the finger came
   * down -- so by the time it lifted the menu was gone, and the lift landed on
   * whatever was underneath: a photo in the thread opened. Carter: "if there
   * is an image behind the blur I tap on it opens the image - it shouldnt".
   * Taken by a full tap on a scrim that covers the screen, nothing under it
   * ever hears it.
   */
  useEffect(() => {
    if (!picker || picking) return;

    const dismiss = () => setPicker(false);
    // Deferred, or anything still settling from the hold closes it at once.
    const armed = window.setTimeout(() => {
      window.addEventListener('scroll', dismiss, true);
    }, 0);

    return () => {
      window.clearTimeout(armed);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [picker, picking]);

  // A poll is not something said, so it gets no bubble and none of the
  // press-and-hold machinery below. It has its own controls.
  if (data.kind === 'poll') {
    return <PollRow message={message} uid={uid} users={users} />;
  }

  /*
   * A location is two numbers and no map. Storing a picture of one would mean
   * a key to a map service and bytes per message for something every phone
   * already has an app for — so it opens theirs, which is what the person
   * receiving it was going to do with it anyway.
   */
  if (data.kind === 'location' && data.place) {
    const { lat, lng } = data.place;
    return (
      <a
        className="place-row"
        href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
        target="_blank"
        rel="noreferrer"
      >
        <span className="attach-icon">
          <IconPin size={18} />
        </span>
        <span className="col grow" style={{ gap: 2, alignItems: 'flex-start', minWidth: 0 }}>
          <span className="bold" style={{ fontSize: 15 }}>
            {users[data.authorUid]?.displayName ?? 'Someone'} shared a location
          </span>
          <span className="tiny dim">
            {lat.toFixed(4)}, {lng.toFixed(4)} · opens in Maps
          </span>
        </span>
        <IconForward size={15} />
      </a>
    );
  }

  if (data.kind === 'system') {
    return (
      <div
        className="row"
        style={{
          gap: 10,
          padding: '12px 14px',
          borderRadius: 'var(--r-sm)',
          background: 'var(--warn-tint)',
          alignItems: 'flex-start',
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            marginTop: 6,
            borderRadius: 'var(--r-pill)',
            background: 'var(--warn)',
            flex: '0 0 auto',
          }}
        />
        <span className="small" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
          {data.text}
        </span>
      </div>
    );
  }


  const mine = data.authorUid === uid;
  const author = users[data.authorUid];
  const myReactions = emojisOf(data.likes?.[uid]);
  const tally = tallyReactions(data.likes);
  const [who, setWho] = useState<DOMRect | null>(null);
  const hold = useHold((at) => setWho(at), LONG_PRESS_MS);

  const react = (emoji: string) => {
    const next = myReactions.includes(emoji)
      ? myReactions.filter((each) => each !== emoji)
      : [...myReactions, emoji];
    void setMessageReaction(message.id, uid, next).catch(() => undefined);
    // Picking one is the whole errand, so the menu goes with it rather than
    // waiting to be tapped away.
    setPicker(false);
  };

  /*
   * What there is to copy. A poll or a location is a thing the app draws
   * rather than something anyone said, so neither gets the button.
   */
  const shots = photosOf(data);
  const copyable = Boolean(data.text?.trim()) || shots.length > 0;

  const copy = () => {
    // The first picture, where there are several. The clipboard holds one
    // image, and the one at the front is the one the album is showing.
    void copyMessage(data.text, shots[0] ? urlFor(shots[0]) : null)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => {
          setCopied(false);
          setPicker(false);
        }, 900);
      })
      .catch(() => undefined);
  };

  // One gesture handler for both interactions: a hold reacts, a rightward drag
  // replies. They are mutually exclusive, so the first one to qualify cancels
  // the other. Vertical movement belongs to the scroller and cancels both.
  const stop = () => {
    if (gesture.current) window.clearTimeout(gesture.current.timer);
    gesture.current = null;
  };

  const onPointerDown = (event: ReactPointerEvent) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    stop();
    // Selecting, every press on the message is the phone's: its handles, its
    // long press on a word. None of them is a swipe, a hold or a tap here.
    if (selecting) return;
    longFired.current = false;
    gesture.current = {
      x: event.clientX,
      y: event.clientY,
      swiping: false,
      timer: window.setTimeout(() => {
        stop();
        longFired.current = true;
        dragged.current = 0;
        setDrag(0);
        setPicker(true);
      }, LONG_PRESS_MS),
    };
  };

  /** Whether some of this message's words are selected right now. */
  const selectingHere = () => {
    const selection = window.getSelection();
    return Boolean(
      selection &&
        !selection.isCollapsed &&
        held.current &&
        selection.anchorNode &&
        held.current.contains(selection.anchorNode),
    );
  };

  const onPointerMove = (event: ReactPointerEvent) => {
    const current = gesture.current;
    if (!current) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;

    if (!current.swiping) {
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
      // Dragging a selection's handle is a press on this message too, and it
      // dragged the whole message off towards a reply. While any of its words
      // are selected, or its bar is open, a drag is never a swipe.
      if (dx < SLOP || Math.abs(dy) > Math.abs(dx) || picker || selectingHere()) {
        stop();
        return;
      }
      window.clearTimeout(current.timer);
      current.swiping = true;
    }

    dragged.current = Math.max(0, Math.min(dx, DRAG_MAX));
    setDrag(dragged.current);
  };

  const onPointerUp = (event: ReactPointerEvent) => {
    // Only a press that began on this message ends in a tap on it. A finger
    // that came down somewhere else -- on the blur round a held message, say,
    // which then went -- and lifted over a photo here opened the photo.
    const began = gesture.current !== null;
    const swiped = gesture.current?.swiping ?? false;
    const photo = tappedPhoto.current;
    tappedPhoto.current = null;
    stop();
    if (!began) {
      dragged.current = 0;
      setDrag(0);
      return;
    }
    if (swiped && dragged.current >= REPLY_AT) onReply?.();
    // Neither a swipe nor a hold, so it was a tap: open the photo it landed
    // on, or show and hide the time.
    else if (!swiped && !longFired.current) {
      // A tap on a clip is the clip's own, to play it, never the time.
      const onClip = Boolean((event.target as Element).closest?.('video'));
      if (photo !== null) setViewing(photo);
      else if (!onClip) setShowTime((shown) => !shown);
    }
    dragged.current = 0;
    setDrag(0);
  };

  const onPointerCancel = () => {
    tappedPhoto.current = null;
    stop();
    dragged.current = 0;
    setDrag(0);
  };

  /**
   * The message itself: drawn in its row, and again lifted into the middle of
   * the screen while it is held. There a tap on a photo opens nothing --
   * the hold is about the message, and the menu is right there.
   */
  const body = (lifted: boolean) =>
    data.deletedAt ? (
      <span className="bubble bubble--gone">Message deleted</span>
    ) : data.kind === 'media' ? (
      // A caption belongs to the message, not beside it. Most of the
      // GroupMe history sent words and pictures together, and until
      // this was here the words were simply never drawn.
      <span
        className="col"
        style={{ gap: 4, alignItems: mine ? 'flex-end' : 'flex-start', minWidth: 0 }}
      >
        <MediaBubble
          message={message}
          onTapPhoto={(i) => {
            if (!lifted) tappedPhoto.current = i;
          }}
        />
        {data.text?.trim() && (
          <span
            className={[
              'bubble',
              'bubble--caption',
              mine ? 'bubble--mine' : '',
              tally.length > 0 ? 'bubble--reacted' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {withMentions(data.text, [
              CHAT_NAME,
              ...Object.values(users).map((member) => member.displayName),
            ])}
          </span>
        )}
      </span>
    ) : (
      <span
        className={[
          'bubble',
          mine ? 'bubble--mine' : '',
          // Room at the bottom for the chips that tuck over this edge.
          tally.length > 0 && !lifted ? 'bubble--reacted' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {withLinks(
          withMentions(data.text ?? '', [
            CHAT_NAME,
            ...Object.values(users).map((member) => member.displayName),
          ]),
        )}
        {data.preview && <LinkCard preview={data.preview} />}
      </span>
    );

  return (
    <div
      id={`m-${message.id}`}
      className={mine ? 'row' : 'row msg-in'}
      style={{
        gap: 10,
        alignItems: 'flex-start',
        flexDirection: mine ? 'row-reverse' : 'row',
      }}
    >
      {/* Out of the row's flow and pinned to the bubble's bottom-left corner,
          so the face rests on the message rather than standing beside it. */}
      {!mine && (
        <span className="msg-face">
          <Avatar name={author?.displayName ?? '?'} photoURL={avatarOf(author)} size={34} />
        </span>
      )}
      <div
        className="col"
        style={{ gap: 4, alignItems: mine ? 'flex-end' : 'flex-start', minWidth: 0 }}
      >
        {/* The time only on request: every message carrying one is clutter. */}
        <span className="tiny muted">
          {mine ? 'You' : (author?.displayName ?? 'Someone')}
          {showTime ? ` · ${clockTime(data.createdAt)}` : ''}
          {data.editedAt && !data.deletedAt ? ' · edited' : ''}
        </span>

        {/*
          The quote of what this answers -- only when there is something to
          quote. It used to stand in for a message not loaded yet with
          "Earlier message, scroll up to load it", which could not be tapped
          to anywhere and, for one deleted outright, never came true. Snaps
          never reach here: a snap back is drawn in its exchange, and a snap
          on its own in its sender's chip.
        */}
        {parent && (
          <button
            className="reply-rail"
            aria-label={`Go to the message from ${users[parent.data.authorUid]?.displayName ?? 'Someone'}`}
            onClick={() =>
              document
                .getElementById(`m-${parent.id}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
            style={{
              flexDirection: mine ? 'row-reverse' : 'row',
              textAlign: mine ? 'right' : 'left',
            }}
          >
            <span className="reply-rail-tick" />
            <QuoteThumb message={parent} size={24} />
            <span className="reply-rail-text">
              <span className="reply-rail-who">
                {users[parent.data.authorUid]?.displayName ?? 'Someone'}
              </span>{' '}
              {quoteOf(parent)}
            </span>
          </button>
        )}

        <div className="swipe-track">
          {onReply && (
            <span
              className="swipe-hint"
              aria-hidden="true"
              style={{ opacity: Math.min(drag / REPLY_AT, 1) }}
            >
              <IconForward size={16} />
            </span>
          )}
          <div
            ref={held}
            className={[
              'row',
              'swipe-body',
              picker ? 'is-held' : '',
              selecting ? 'is-selecting' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              gap: 6,
              // A message sits on its baseline beside the face.
              alignItems: 'flex-end',
              flexDirection: mine ? 'row-reverse' : 'row',
              // While held the scale lives in the class, so the inline rule
              // has to stand aside or it would cancel the lift.
              transform: drag ? `translateX(${drag}px)` : undefined,
              transition: gesture.current
                ? 'none'
                : 'transform 220ms var(--ease-spring)',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onContextMenu={(event) => {
              // While selecting, a long press on the words is the phone's own
              // -- refusing it stops Android's selection dead. Otherwise a
              // long press is only ever the reaction bar.
              const target = event.target as Element;
              if (selecting && target.closest('.bubble') && !target.closest('.link-card')) return;
              event.preventDefault();
            }}
          >
            {body(false)}
          </div>

          {/*
            * Held: the message lifted out of the thread into the middle of
            * the screen, its reactions over it and what can be done with it
            * under it, all at a size for a thumb. Carter: "can you expand it
            * so that the buttons and reactions that come up with it are
            * bigger - bring the message to the center of the screen". On the
            * page rather than in the row, which is scaled and slid and would
            * make anything fixed inside it measure from itself.
            */}
          {picker &&
            createPortal(
              <div className={armed ? 'hold-focus is-armed' : 'hold-focus'} data-mine={mine ? 'true' : 'false'}>
                <span className="hold-scrim" aria-hidden="true" onClick={() => setPicker(false)} />
                <div
                  className="hold-card"
                  // The layer is drawn at the screen's own size, so these are
                  // the same pixels the thread's edge was measured in.
                  style={
                    liftedEdge !== null
                      ? mine
                        ? { paddingRight: Math.max(HOLD_EDGE_PX, liftedEdge) }
                        : { paddingLeft: Math.max(HOLD_EDGE_PX, liftedEdge) }
                      : undefined
                  }
                >
                  {/* Stays open after a pick, so several can be added in a row. */}
                  <div className="reaction-pop" data-mine={mine ? 'true' : 'false'}>
                    <ReactionBar mine={myReactions} onPick={react} onPickingChange={setPicking} />
                  </div>
                  <div
                    className="hold-message"
                    style={liftedWidth ? { width: liftedWidth } : undefined}
                  >
                    {body(true)}
                  </div>
              {!data.deletedAt && (copyable || mine) && (
                <span className="message-actions">
                  {/* Anybody's message, not only your own: copying what
                      somebody else said is most of what copying is for. */}
                  {copyable && (
                    <button type="button" onClick={copy}>
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  )}
                  {data.text?.trim() && (
                    <button
                      type="button"
                      onClick={() => {
                        setPicker(false);
                        setSelecting(true);
                      }}
                    >
                      Select
                    </button>
                  )}
                  {/* Words or a photo: something worth keeping over the thread. */}
                  {copyable && onPin && (
                    <button
                      type="button"
                      onClick={() => {
                        setPicker(false);
                        if (pinnedHere) onUnpin?.();
                        else onPin();
                      }}
                    >
                      {pinnedHere ? 'Unpin' : 'Pin'}
                    </button>
                  )}
                  {/* The words with a photo, a clip or a file are as much yours to fix. */}
                  {mine && (data.kind === 'text' || data.kind === 'media') && (
                    <button
                      type="button"
                      onClick={() => {
                        setPicker(false);
                        onEdit?.();
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {mine && (
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => {
                        setPicker(false);
                        onDelete?.();
                      }}
                    >
                      Delete
                    </button>
                  )}
                </span>
              )}
                </div>
              </div>,
              document.body,
            )}
        </div>

        {tally.length > 0 && (
          <span className="reaction-row">
            {tally.map(({ emoji, count }) => (
              <button
                key={emoji}
                type="button"
                className={
                  myReactions.includes(emoji) ? 'reaction-chip is-mine' : 'reaction-chip'
                }
                aria-label={
                  myReactions.includes(emoji) ? `Remove your ${emoji}` : `React ${emoji}`
                }
                {...hold.handlers}
                // A hold has already been answered by opening the panel; the
                // click that the finger lifting sends afterwards is not a
                // second instruction to react.
                onClick={() => {
                  if (hold.consumed()) return;
                  react(emoji);
                }}
              >
                <span>{emoji}</span>
                {count > 1 && <span className="tiny bold">{count}</span>}
              </button>
            ))}
          </span>
        )}

        {who && (
          <ReactedBy
            byPerson={data.likes}
            nameOf={(each) => users[each]?.displayName ?? 'Someone'}
            at={who}
            onClose={() => setWho(null)}
          />
        )}

      </div>

      {viewing !== null && (
        <MediaViewer
          items={shots.map((media) => ({
            key: media.key,
            src: urlFor(media),
            preview: media.small ? urlFor(shownMedia(media)) : media.blur,
            who: mine ? 'You' : (author?.displayName ?? 'Someone'),
            at: toDate(data.createdAt),
          }))}
          index={viewing}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onSave={(i) => saveToPhone(shots[i])}
        />
      )}
    </div>
  );
}

/** 95 seconds reads as 1:35, which is how long a voice note feels. */
function formatDuration(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
}

/** Whole units. A file row wants a size, not a measurement. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1e6) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1e6).toFixed(bytes < 1e7 ? 1 : 0)} MB`;
}

/** The photos of a message, in the order they were attached. */
function photosOf(data: MessageDoc): MediaRef[] {
  // A snap's photo is stored as a photo, but it is for looking at once: the
  // menu's Copy would otherwise hand it to the clipboard to keep.
  if (data.kind === 'snap') return [];
  return (data.media ?? []).filter((m) => m.kind === 'photo');
}

/** At most this many tiles; the last one carries the count of the rest. */
const TILES = 4;

function MediaBubble({
  message,
  onTapPhoto,
}: {
  message: WithId<MessageDoc>;
  onTapPhoto?: (index: number) => void;
}) {
  const data = message.data;
  const photos = photosOf(data);
  const video = (data.media ?? []).find((m) => m.kind === 'video');
  const poster = (data.media ?? []).find((m) => m.kind === 'poster');
  const voice = (data.media ?? []).find((m) => m.kind === 'audio');

  if (data.status === 'processing') {
    return (
      <span
        className="col"
        style={{
          width: 247,
          padding: 16,
          gap: 10,
          borderRadius: 'var(--r-md)',
          background: 'var(--surface)',
        }}
      >
        <span className="row" style={{ gap: 10 }}>
          <Spinner />
          <span className="small bold">Squaring up the video</span>
        </span>
        <span className="tiny muted">It will appear here on its own.</span>
      </span>
    );
  }

  if (data.status === 'failed') {
    return (
      <span className="bubble" style={{ color: 'var(--danger)' }}>
        That video did not go through.
      </span>
    );
  }

  if (voice) {
    return (
      <span
        className="col"
        style={{
          width: 247,
          padding: 12,
          gap: 6,
          borderRadius: 'var(--r-md)',
          background: 'var(--surface)',
        }}
      >
        <span className="row" style={{ gap: 8 }}>
          <span style={{ color: 'var(--accent)', display: 'flex' }}>
            <IconMic size={16} />
          </span>
          <span className="tiny muted grow">
            Voice note{voice.durationSec ? ` · ${formatDuration(voice.durationSec)}` : ''}
          </span>
        </span>
        {/* preload="none" so a thread full of these does not fetch every one
            of them the moment it is scrolled past. */}
        <audio src={urlFor(voice)} controls preload="none" style={{ width: '100%' }} />
      </span>
    );
  }

  if (video) return <VideoBubble video={video} poster={poster} />;

  // Nothing to draw, so the name and the size are the whole of it. Opened
  // through the gateway like every other object, which is where the session
  // cookie that authorizes it lives.
  const file = (data.media ?? []).find((each) => each.kind === 'file');
  if (file) {
    return (
      <a className="file-row" href={urlFor(file)} target="_blank" rel="noreferrer" download>
        <span className="attach-icon">
          <IconFile size={18} />
        </span>
        <span className="col grow" style={{ gap: 2, alignItems: 'flex-start', minWidth: 0 }}>
          <span className="file-name">{file.name ?? 'File'}</span>
          <span className="tiny dim">{formatBytes(file.bytes)}</span>
        </span>
        <IconDownload size={16} />
      </a>
    );
  }

  if (photos.length === 0) return <span className="bubble muted">(empty)</span>;

  const shown = photos.slice(0, TILES);
  const rest = photos.length - shown.length;

  return (
    <span
      className="thread-media"
      style={{
        display: 'grid',
        gridTemplateColumns: photos.length > 1 ? '1fr 1fr' : '1fr',
        gap: 4,
      }}
    >
      {shown.map((media, i) => {
        // Three reads as one above two, so the odd one out leads rather than
        // leaving a gap beside it.
        const wide = photos.length === 3 && i === 0;
        const last = i === shown.length - 1;

        return (
          <span
            key={media.key}
            // Not a button: the row owns press-and-hold and swipe-to-reply,
            // and a button here would take the gesture for itself. It reports
            // the tap instead and lets the row decide what the tap was.
            onPointerUp={() => onTapPhoto?.(i)}
            style={{
              position: 'relative',
              display: 'block',
              gridColumn: wide ? 'span 2' : undefined,
            }}
          >
            {/*
              * One on its own is shown whole, at its own shape. Several share
              * a grid of squares, cropped, the way a phone's own gallery does.
              */}
            <RetryingImg
              src={urlFor(shownMedia(media))}
              preview={media.blur}
              alt=""
              loading="lazy"
              className={photos.length > 1 ? undefined : 'thread-media-fit'}
              style={
                photos.length > 1
                  ? {
                      display: 'block',
                      width: '100%',
                      aspectRatio: wide ? '2 / 1' : '1 / 1',
                      objectFit: 'cover',
                      borderRadius: 'var(--r-sm)',
                      backgroundColor: 'var(--well)',
                    }
                  : fitTo(shapeOf(media) ?? 3 / 4)
              }
            />
            {last && rest > 0 && (
              <span
                className="center"
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 'var(--r-sm)',
                  background: 'rgba(8,9,12,0.55)',
                  color: '#fff',
                  fontSize: 19,
                  fontWeight: 700,
                  pointerEvents: 'none',
                }}
              >
                +{rest}
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

/**
 * A video in the thread, without native controls until it is playing.
 *
 * `controls` is what made a video the one kind of message you could not press
 * and hold. The browser's own media controls take the gesture for themselves,
 * and on Android a long press over a video opens Chrome's own menu and cancels
 * the pointer, so the hold never completed — there was no way to reach edit or
 * delete on anything with a video in it.
 *
 * So there are none until you start it. Every press then lands on the element
 * itself and reaches the thread, which is where press-and-hold lives. Once it
 * is playing the real controls appear, because scrubbing matters more than
 * holding on something you are already watching.
 */
/** A picture's width over its height, when what was stored about it knows it. */
function shapeOf(media: MediaRef | undefined): number | null {
  return media && media.width > 0 && media.height > 0 ? media.width / media.height : null;
}

/**
 * Sized to the picture: its own shape, and as wide as that shape allows under
 * the height cap -- never wider, so a tall photo is not set in the middle of a
 * message-wide box with bars down both sides (theme.css, .thread-media-fit).
 */
function fitTo(ratio: number): CSSProperties {
  return { aspectRatio: String(ratio), '--ar': String(ratio) } as CSSProperties;
}

function VideoBubble({ video, poster }: { video: MediaRef; poster?: MediaRef }) {
  const [playing, setPlaying] = useState(false);
  const clip = useRef<HTMLVideoElement>(null);
  /*
   * Its shape: from what was stored, or -- for a clip stored before its size
   * was -- from the clip itself once its first details arrive. Until then an
   * upright phone clip, which is what nearly every one is.
   */
  const [measured, setMeasured] = useState<number | null>(null);
  const stored = shapeOf(video) ?? shapeOf(poster);
  const shape = stored ?? measured ?? 9 / 16;
  const still = poster ? urlFor(poster) : undefined;

  /*
   * And from the poster, which is the first frame at the clip's own shape and
   * is drawn whether or not the clip itself is ever asked about. Waiting on
   * the clip alone left a wide one standing in a tall box with grey above and
   * below it, its details never having arrived; the poster always does,
   * because it is what is on screen.
   */
  useEffect(() => {
    if (stored !== null || !still) return;
    const frame = new Image();
    frame.onload = () => {
      if (frame.naturalWidth > 0 && frame.naturalHeight > 0) {
        setMeasured((known) => known ?? frame.naturalWidth / frame.naturalHeight);
      }
    };
    frame.src = still;
    return () => {
      frame.onload = null;
    };
  }, [stored, still]);

  return (
    <span className="thread-media" style={{ position: 'relative', display: 'block' }}>
      {/*
        * Without its controls a video does nothing on a tap, so the tap went
        * on to the row and showed the time instead. Until it has started, a
        * tap starts it; from then on its own controls take over.
        */}
      <video
        ref={clip}
        onClick={() => {
          if (!playing) void clip.current?.play().catch(() => undefined);
        }}
        src={urlFor(video)}
        poster={still}
        controls={playing}
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onLoadedMetadata={(event) => {
          const { videoWidth, videoHeight } = event.currentTarget;
          if (videoWidth > 0 && videoHeight > 0) setMeasured(videoWidth / videoHeight);
        }}
        className="thread-media-fit"
        style={fitTo(shape)}
      />

      {!playing && (
        /*
          * Drawn on, not pressed: a button here would swallow the gesture,
          * which is the whole thing being fixed. The press underneath it goes
          * to the video and on up to the thread.
          */
        <span
          className="center"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            color: 'rgba(255,255,255,0.86)',
          }}
        >
          <IconPlay size={30} />
        </span>
      )}
    </span>
  );
}

